import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import { Plus, Trash2 } from 'lucide-react'

interface Community {
  id: number
  name: string
}

interface Answer {
  text: string
}

interface SurveyData {
  id?: number
  title: string
  question: string
  answers: Answer[]
  multiple_choice: boolean
  points_reward: number
  start_date: string
  end_date: string
  published: boolean
  community_id: number | string
}

interface SurveyFormProps {
  survey?: SurveyData
  communities: Community[]
  suggestedStartDate?: string
  onSubmit: string
  isEdit?: boolean
}

export function SurveyForm({
  survey,
  communities,
  suggestedStartDate,
  onSubmit,
  isEdit = false
}: SurveyFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    title: survey?.title || '',
    question: survey?.question || '',
    answers: survey?.answers || [{ text: '' }, { text: '' }],
    multiple_choice: survey?.multiple_choice || false,
    points_reward: survey?.points_reward || 5,
    start_date: survey?.start_date || suggestedStartDate || '',
    end_date: survey?.end_date || '',
    published: survey?.published || false,
    community_id: survey?.community_id?.toString() || ''
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    const hasEmptyAnswers = data.answers.some(a => !a.text.trim())
    if (hasEmptyAnswers) {
      alert('Toutes les réponses doivent être remplies')
      return
    }

    if (isEdit && survey?.id) {
      put(onSubmit)
    } else {
      post(onSubmit)
    }
  }

  const updateAnswer = (index: number, text: string) => {
    const newAnswers = [...data.answers]
    newAnswers[index] = { text }
    setData('answers', newAnswers)
  }

  const addAnswer = () => {
    if (data.answers.length >= 10) return
    setData('answers', [...data.answers, { text: '' }])
  }

  const removeAnswer = (index: number) => {
    if (data.answers.length <= 2) return
    setData('answers', data.answers.filter((_, i) => i !== index))
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6">
        <div className="space-y-8">
          {/* Section 1: Informations Générales */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Informations Générales</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-1">
                  Titre *
                </label>
                <Input
                  id="title"
                  type="text"
                  maxLength={200}
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  placeholder="Ex: Votre avis sur les nouvelles fonctionnalités"
                  required
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                <p className="mt-1 text-xs text-text-gray">{data.title.length}/200 caractères</p>
              </div>

              <div>
                <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                  Communauté *
                </label>
                <Select
                  id="community_id"
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                  required
                >
                  <option value="">Sélectionner une communauté</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </Select>
                {errors.community_id && <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>}
              </div>
            </div>
          </div>

          {/* Section 2: Question et Réponses */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Question et Réponses</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="question" className="block text-sm font-medium text-text-dark mb-1">
                  Question *
                </label>
                <Textarea
                  id="question"
                  value={data.question}
                  onChange={(e) => setData('question', e.target.value)}
                  placeholder="Quelle fonctionnalité aimeriez-vous voir ajoutée ?"
                  rows={3}
                  required
                />
                {errors.question && <p className="mt-1 text-sm text-red-600">{errors.question}</p>}
              </div>

              {data.answers.map((answer, index) => (
                <div key={index}>
                  <label htmlFor={`answer_${index}`} className="block text-sm font-medium text-text-dark mb-1">
                    Réponse {index + 1} *
                  </label>
                  <div className="flex gap-2 items-start">
                    <Input
                      id={`answer_${index}`}
                      type="text"
                      value={answer.text}
                      onChange={(e) => updateAnswer(index, e.target.value)}
                      placeholder={`Réponse ${index + 1}`}
                      required
                      className="flex-1"
                    />
                    {data.answers.length > 2 && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="p-2 text-red-600 hover:bg-red-50"
                        onClick={() => removeAnswer(index)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {data.answers.length < 10 && (
                <Button type="button" variant="secondary" onClick={addAnswer} className="flex items-center gap-2">
                  <Plus size={16} />
                  Ajouter une réponse
                </Button>
              )}

              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={data.multiple_choice}
                  onChange={(e) => setData('multiple_choice', e.target.checked)}
                  className="mt-1 text-tessan-primary"
                />
                <div>
                  <p className="font-medium text-text-dark">Choix multiples</p>
                  <p className="text-sm text-text-gray">
                    Permet aux utilisateurs de sélectionner plusieurs réponses.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Section 3: Planification */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Planification</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-text-dark mb-1">
                  Date de début *
                </label>
                <Input
                  id="start_date"
                  type="date"
                  value={data.start_date}
                  onChange={(e) => setData('start_date', e.target.value)}
                  required
                />
                {errors.start_date && <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>}
              </div>

              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-text-dark mb-1">
                  Date de fin *
                </label>
                <Input
                  id="end_date"
                  type="date"
                  value={data.end_date}
                  onChange={(e) => setData('end_date', e.target.value)}
                  required
                />
                {errors.end_date && <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>}
              </div>
            </div>
          </div>

          {/* Section 4: Points */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Points</h3>
            <div>
              <label htmlFor="points_reward" className="block text-sm font-medium text-text-dark mb-1">
                Points pour participation
              </label>
              <Input
                id="points_reward"
                type="number"
                min={0}
                max={100}
                value={data.points_reward}
                onChange={(e) => setData('points_reward', parseInt(e.target.value) || 0)}
              />
              {errors.points_reward && <p className="mt-1 text-sm text-red-600">{errors.points_reward}</p>}
              <p className="mt-1 text-xs text-text-gray">
                Points attribués à chaque participant (0 = pas de points)
              </p>
            </div>
          </div>

          {/* Section 5: Publication */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Publication</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.published}
                onChange={(e) => setData('published', e.target.checked)}
                className="mt-1 text-tessan-primary"
              />
              <div>
                <p className="font-medium text-text-dark">Publier immédiatement</p>
                <p className="text-sm text-text-gray">
                  Si coché, le sondage sera visible par les utilisateurs. Sinon, il sera sauvegardé en brouillon.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => history.back()}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Sauvegarder'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
